import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import "./styles/Stats.css";

import graphics1 from "../medias/png/graphics/1.png";
import graphics2 from "../medias/png/graphics/2.png";

const API_URL = process.env.REACT_APP_API_URL;

const asInt = (v) => {
  if (Number.isInteger(v)) return v;
  if (typeof v === "number") return Math.trunc(v);
  const n = parseInt(String(v ?? "0"), 10);
  return Number.isFinite(n) ? n : 0;
};

const normalize = (s) => String(s ?? "").toLowerCase().trim();

const mapPickRow = (m) => ({
  cyclistId: asInt(m?.cyclistId ?? m?.id),
  name: String(m?.name ?? ""),
  pickedBy: asInt(m?.pickedBy ?? m?.picked ?? m?.count),
});

const TitleWithYellowBar = ({ children }) => {
  return (
    <div className="title-yellowbar">
      <h2 className="title-yellowbar__text">{children}</h2>
      <span className="title-yellowbar__bar" aria-hidden="true" />
    </div>
  );
};

const PickCountTable = ({
  loading,
  error,
  rows,
  onRetry,
  colName,
  colPicked,
  emptyText,
  errorText,
  retryText,
}) => {
  if (loading) {
    return (
      <div className="picktable picktable--center">
        <div className="spinner" aria-label="loading" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="picktable picktable--center">
        <p className="picktable__error">{errorText(String(error))}</p>
        <button className="btn-retry" onClick={onRetry} type="button">
          ↻ {retryText}
        </button>
      </div>
    );
  }

  if (!rows.length) {
    return <p className="picktable__empty">{emptyText}</p>;
  }

  return (
    <div className="picktable">
      <div className="picktable__header">
        <div className="picktable__col picktable__col--name">{colName}</div>
        <div className="picktable__col picktable__col--picked">{colPicked}</div>
      </div>

      <div className="picktable__divider" />

      {rows.map((r) => (
        <React.Fragment key={r.cyclistId || `${r.name}-${r.pickedBy}`}>
          <div className="picktable__row">
            <div className="picktable__cell picktable__cell--name" title={r.name}>
              {r.name}
            </div>
            <div className="picktable__cell picktable__cell--picked">{r.pickedBy}</div>
          </div>
          <div className="picktable__divider" />
        </React.Fragment>
      ))}
    </div>
  );
};

const Stats = () => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");

  const abortRef = useRef(null);

  const fetchPickCounts = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/cyclistsPickCount`, {
        method: "GET",
        signal: ac.signal,
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const decoded = await res.json();

      const list =
        decoded && typeof decoded === "object" && Array.isArray(decoded.cyclists)
          ? decoded.cyclists
          : Array.isArray(decoded)
            ? decoded
            : [];

      const mapped = list
        .filter((x) => x && typeof x === "object")
        .map(mapPickRow)
        .sort((a, b) => {
          const c = b.pickedBy - a.pickedBy;
          if (c !== 0) return c;
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        });

      setRows(mapped);
      setLoading(false);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPickCounts();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchPickCounts]);

  const filtered = useMemo(() => {
    const q = normalize(query);
    if (!q) return rows;
    return rows.filter((r) => normalize(r.name).includes(q));
  }, [rows, query]);

  return (
    <main className="stats">
      <h1>
        <br />
        {t("stats.title")}
      </h1>

      <p>{t("stats.description")}</p>

      <div className="stats-graphics-grid">
        <img src={graphics1} alt={t("stats.graphicsAlt", { n: 1 })} />
        <img src={graphics2} alt={t("stats.graphicsAlt", { n: 2 })} />
      </div>

      <section className="stats-picks">
        <TitleWithYellowBar>{t("stats.pickCountTitle")}</TitleWithYellowBar>

        <div className="picksearch">
          <span className="picksearch__icon" aria-hidden="true">
            🔎
          </span>
          <input
            className="picksearch__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("stats.pickCountSearchHint")}
            autoComplete="off"
          />
        </div>

        <PickCountTable
          loading={loading}
          error={error}
          rows={filtered}
          onRetry={fetchPickCounts}
          colName={t("stats.pickCountColName")}
          colPicked={t("stats.pickCountColPicked")}
          emptyText={t("stats.pickCountEmpty")}
          errorText={(msg) => t("stats.pickCountErrorGeneric", { msg })}
          retryText={t("common.retry")}
        />
      </section>
    </main>
  );
};

export default Stats;
